import { supabase, SUPABASE_PAGE_SIZE } from '../supabase';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Shift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { useMemo, useCallback } from 'react';
import { isDST } from '../../../utils/time';
import { OT_SHIFT_CODES_KEYS } from '@shiftly/shared/src/shiftCodes';
import { calculatePercentageOfEarnings } from '../../../utils/statsCalculator';
import { useUsers } from '../../supabase/users/supabaseUserService';
import SupabaseShiftLoggingService from '../logging/supabaseShiftLoggingService';

export type ShiftFilter = {
  field: keyof Shift | string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not-in' | 'is';
  value: any;
};

export type ShiftSort = {
  field: keyof Shift | string;
  direction: 'asc' | 'desc';
};

export type GetShiftsParams = {
  filters?: ShiftFilter[];
  sort?: ShiftSort[];
  enabled?: boolean;
  limit?: number;
};

export function convertDateFields(data: any): Shift {
  return {
    ...data,
    start_date_time: DateTime.fromISO(data.start_date_time, { zone: 'UTC' }),
    end_date_time: DateTime.fromISO(data.end_date_time, { zone: 'UTC' }),
    clock_in_time: data.clock_in_time ? DateTime.fromISO(data.clock_in_time, { zone: 'UTC' }) : undefined,
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' }),
    verified_at: data.verified_at ? DateTime.fromISO(data.verified_at, { zone: 'UTC' }) : undefined,
  } as Shift;
}

const SupabaseShiftService = {
  async getShifts({ filters = [], sort = [], limit }: GetShiftsParams): Promise<Shift[]> {
    try {
      const inFilters = filters.filter(f => f.operator === 'in');
      const largeInFilter = inFilters.find(f => Array.isArray(f.value) && f.value.length > 10);

      if (largeInFilter && Array.isArray(largeInFilter.value)) {
        // Optimized batching for large 'in' queries
        const CHUNK_SIZE = 100;
        const MAX_CONCURRENT_QUERIES = 10;

        const values = largeInFilter.value;
        const chunks = Array.from(
          { length: Math.ceil(values.length / CHUNK_SIZE) },
          (_, i) => values.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
        );

        const processChunk = async (chunkValues: any[]): Promise<Shift[]> => {
          // Create filters for this chunk
          const chunkFilters = filters.map(f =>
            f === largeInFilter ? { ...f, value: chunkValues } : f
          );

          // Get total count first to optimize pagination
          let countQuery = supabase.from('shifts').select('*', { count: 'exact', head: true });
          chunkFilters.forEach(filter => {
            switch (filter.operator) {
              case 'eq': countQuery = countQuery.eq(filter.field, filter.value); break;
              case 'neq': countQuery = countQuery.neq(filter.field, filter.value); break;
              case 'gt': countQuery = countQuery.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'gte': countQuery = countQuery.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'lt': countQuery = countQuery.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'lte': countQuery = countQuery.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
              case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
              case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
            }
          });

          const { count } = await countQuery;
          if (!count || count === 0) return [];

          // Calculate optimal page count for parallel requests
          const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);
          const pagePromises: Promise<Shift[]>[] = [];

          // Create all page requests in parallel
          for (let page = 0; page < totalPages; page++) {
            const pagePromise = (async (offset: number): Promise<Shift[]> => {
              let query = supabase.from('shifts').select('*');

              // Apply filters
              chunkFilters.forEach(filter => {
                switch (filter.operator) {
                  case 'eq':
                    query = query.eq(filter.field, filter.value);
                    break;
                  case 'neq':
                    query = query.neq(filter.field, filter.value);
                    break;
                  case 'gt':
                    query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'gte':
                    query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lt':
                    query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lte':
                    query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'in':
                    query = query.in(filter.field, filter.value);
                    break;
                  case 'not-in':
                    query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                    break;
                  case 'is':
                    query = query.is(filter.field, filter.value);
                    break;
                }
              });

              // Apply sorting with stable secondary sort
              if (sort.length > 0) {
                sort.forEach(sortOption => {
                  query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                });
                // Add stable secondary sort if not already sorting by id
                if (!sort.some(s => s.field === 'id')) {
                  query = query.order('id', { ascending: true });
                }
              } else {
                query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
              }

              // Add pagination
              query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

              const { data, error } = await query;

              if (error) throw error;

              return (data || []) as Shift[];
            })(page * SUPABASE_PAGE_SIZE);

            pagePromises.push(pagePromise);
          }

          // Execute all page requests in parallel
          const pageResults = await Promise.all(pagePromises);
          return pageResults.flat();
        };

        // Process chunks with optimized concurrency
        const results: Shift[] = [];

        for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_QUERIES) {
          const currentBatch = chunks.slice(i, i + MAX_CONCURRENT_QUERIES);
          const batchPromises = currentBatch.map(processChunk);

          const batchResults = await Promise.all(batchPromises);
          results.push(...batchResults.flat());
        }
        return results.map((shift: Shift) => (convertDateFields(shift)));
      }

      // Standard single query path
      if (limit) {
        // Single query with limit
        let query = supabase.from('shifts').select('*');

        // Apply filters
        filters.forEach(filter => {
          switch (filter.operator) {
            case 'eq':
              query = query.eq(filter.field, filter.value);
              break;
            case 'neq':
              query = query.neq(filter.field, filter.value);
              break;
            case 'gt':
              query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
              break;
            case 'gte':
              query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
              break;
            case 'lt':
              query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
              break;
            case 'lte':
              query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
              break;
            case 'in':
              query = query.in(filter.field, filter.value);
              break;
            case 'not-in':
              query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
              break;
            case 'is':
              query = query.is(filter.field, filter.value);
              break;
          }
        });

        // Apply sorting with stable secondary sort
        if (sort.length > 0) {
          sort.forEach(sortOption => {
            query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
          });
          // Add stable secondary sort if not already sorting by id
          if (!sort.some(s => s.field === 'id')) {
            query = query.order('id', { ascending: true });
          }
        } else {
          query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
        }

        query = query.limit(limit);

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching shifts:', error);
          throw error;
        }

        return ((data || []) as Shift[]).map((shift: Shift) => (convertDateFields(shift)));
      } else {
        const SUPABASE_PAGE_SIZE = 2000;
        const MAX_CONCURRENT_PAGES = 10;

        // Get total count first
        let countQuery = supabase.from('shifts').select('*', { count: 'exact', head: true });
        filters.forEach(filter => {
          switch (filter.operator) {
            case 'eq': countQuery = countQuery.eq(filter.field, filter.value); break;
            case 'neq': countQuery = countQuery.neq(filter.field, filter.value); break;
            case 'gt': countQuery = countQuery.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
            case 'gte': countQuery = countQuery.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
            case 'lt': countQuery = countQuery.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
            case 'lte': countQuery = countQuery.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
            case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
            case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
            case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
          }
        });

        const { count } = await countQuery;
        if (!count || count === 0) return [];

        const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);

        // For large datasets, use parallel pagination
        if (totalPages > 5) {
          const allResults: Shift[] = [];

          for (let batchStart = 0; batchStart < totalPages; batchStart += MAX_CONCURRENT_PAGES) {
            const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PAGES, totalPages);
            const pagePromises: Promise<Shift[]>[] = [];

            for (let page = batchStart; page < batchEnd; page++) {
              const pagePromise = (async (offset: number): Promise<Shift[]> => {
                let query = supabase.from('shifts').select('*');

                // Apply filters
                filters.forEach(filter => {
                  switch (filter.operator) {
                    case 'eq':
                      query = query.eq(filter.field, filter.value);
                      break;
                    case 'neq':
                      query = query.neq(filter.field, filter.value);
                      break;
                    case 'gt':
                      query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                      break;
                    case 'gte':
                      query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                      break;
                    case 'lt':
                      query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                      break;
                    case 'lte':
                      query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                      break;
                    case 'in':
                      query = query.in(filter.field, filter.value);
                      break;
                    case 'not-in':
                      query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                      break;
                    case 'is':
                      query = query.is(filter.field, filter.value);
                      break;
                  }
                });

                // Apply sorting with stable secondary sort
                if (sort.length > 0) {
                  sort.forEach(sortOption => {
                    query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                  });
                  // Add stable secondary sort if not already sorting by id
                  if (!sort.some(s => s.field === 'id')) {
                    query = query.order('id', { ascending: true });
                  }
                } else {
                  query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
                }

                // Add pagination
                query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

                const { data, error } = await query;
                if (error) throw error;

                return (data || []) as Shift[];
              })(page * SUPABASE_PAGE_SIZE);

              pagePromises.push(pagePromise);
            }

            // Execute batch in parallel
            const batchResults = await Promise.all(pagePromises);
            allResults.push(...batchResults.flat());
          }

          return allResults.map((shift: Shift) => (convertDateFields(shift)));
        } else {
          // Use sequential pagination for small datasets
          const allResults: Shift[] = [];
          let hasMore = true;
          let offset = 0;

          while (hasMore) {
            let query = supabase.from('shifts').select('*');

            // Apply filters
            filters.forEach(filter => {
              switch (filter.operator) {
                case 'eq':
                  query = query.eq(filter.field, filter.value);
                  break;
                case 'neq':
                  query = query.neq(filter.field, filter.value);
                  break;
                case 'gt':
                  query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                  break;
                case 'gte':
                  query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                  break;
                case 'lt':
                  query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                  break;
                case 'lte':
                  query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                  break;
                case 'in':
                  query = query.in(filter.field, filter.value);
                  break;
                case 'not-in':
                  query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                  break;
                case 'is':
                  query = query.is(filter.field, filter.value);
                  break;
              }
            });

            // Apply sorting with stable secondary sort
            if (sort.length > 0) {
              sort.forEach(sortOption => {
                query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
              });
              // Add stable secondary sort if not already sorting by id
              if (!sort.some(s => s.field === 'id')) {
                query = query.order('id', { ascending: true });
              }
            } else {
              query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
            }

            // Add pagination
            query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

            const { data, error } = await query;

            if (error) {
              console.error('Error fetching shifts page:', error);
              throw error;
            }

            const shifts = (data || []) as Shift[];
            allResults.push(...shifts);

            // Check if we got fewer results than requested (end of data)
            hasMore = shifts.length === SUPABASE_PAGE_SIZE;
            offset += SUPABASE_PAGE_SIZE;
          }

          return allResults.map((shift: Shift) => (convertDateFields(shift)));
        }
      }
    } catch (error) {
      console.error('Error in getShifts:', error);
      return [];
    }
  },

  async createShift(shift: Omit<Shift, 'id' | 'created_at' | 'updated_at'>, performed_by?: string): Promise<string> {
    try {
      const now = DateTime.utc();
      const { data, error } = await supabase
        .from('shifts')
        .insert({
          fixed_shift_id: shift.fixed_shift_id,
          model_id: shift.model_id,
          user_id: shift.user_id || null,
          trainer_id: shift.trainer_id,
          start_date_time: shift.start_date_time.toISO(),
          end_date_time: shift.end_date_time.toISO(),
          ppv_earned: shift.ppv_earned,
          noppv_earned: shift.noppv_earned,
          livestream_earned: shift.livestream_earned,
          is_livestream_shift: shift.is_livestream_shift,
          shift_code: shift.shift_code,
          submitted: shift.submitted,
          clocked_in: false,
          clock_in_time: shift.clock_in_time?.toISO(),
          verification_required: shift.verification_required,
          verified_by: shift.verified_by,
          verified_at: shift.verified_at?.toISO(),
          created_at: now.toISO(),
          updated_at: now.toISO()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating shift:', error);
        throw error;
      }

      // Get current user for logging
      const currentUser = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Log the shift creation if we have a user
      if (currentUser) {
        try {
          const createdShift: Shift = {
            ...shift,
            id: data.id,
            created_at: now,
            updated_at: now,
          };
          await SupabaseShiftLoggingService.logSalesShiftCreate(createdShift, currentUser);
        } catch (logError) {
          console.error('Error logging shift creation:', logError);
          // Don't throw here - the shift was created successfully
        }
      }

      return data.id;
    } catch (error) {
      console.error('Error in createShift:', error);
      throw error;
    }
  },

  async updateShift(shiftId: string, data: Partial<Shift>, performedBy?: string): Promise<void> {
    try {
      // Get current user for logging
      const currentUser = performedBy || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let before_data: Partial<Shift> | undefined;
      if (currentUser) {
        try {
          const { data: currentShift } = await supabase
            .from('shifts')
            .select('*')
            .eq('id', shiftId)
            .single();
          if (currentShift) {
            before_data = convertDateFields(currentShift);
          }
        } catch (error) {
          console.error('Error fetching shift for logging:', error);
        }
      }

      const update_data: any = {
        updated_at: DateTime.utc().toISO()
      };

      // Convert camelCase fields to snake_case
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'id' || key === 'created_at' || key === 'updated_at') return;

        if (DateTime.isDateTime(value)) {
          update_data[key] = value.toISO();
        } else {
          update_data[key] = value;
        }
      });

      const { error } = await supabase
        .from('shifts')
        .update(update_data)
        .eq('id', shiftId);

      if (error) {
        console.error('Error updating shift:', error);
        throw error;
      }

      // Log the shift update if we have a user and before data
      if (currentUser && before_data) {
        try {
          const afterData = { ...before_data, ...data };
          await SupabaseShiftLoggingService.logSalesShiftUpdate(shiftId, { before: before_data, after: afterData }, currentUser);
        } catch (logError) {
          console.error('Error logging shift update:', logError);
          // Don't throw here - the shift was updated successfully
        }
      }
    } catch (error) {
      console.error('Error in updateShift:', error);
      throw error;
    }
  },

  async deleteShift(shiftId: string, performed_by?: string): Promise<void> {
    try {
      // Get current user for logging
      const currentUser = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let shift_data: Shift | undefined;
      if (currentUser) {
        try {
          const { data: currentShift } = await supabase
            .from('shifts')
            .select('*')
            .eq('id', shiftId)
            .single();
          if (currentShift) {
            shift_data = convertDateFields(currentShift);
          }
        } catch (error) {
          console.error('Error fetching shift for logging:', error);
        }
      }

      const { error } = await supabase
        .from('shifts')
        .delete()
        .eq('id', shiftId);

      if (error) {
        console.error('Error deleting shift:', error);
        throw error;
      }

      // Log the shift deletion if we have a user and shift data
      if (currentUser && shift_data) {
        try {
          await SupabaseShiftLoggingService.logSalesShiftDelete(shift_data, currentUser);
        } catch (logError) {
          console.error('Error logging shift deletion:', logError);
          // Don't throw here - the shift was deleted successfully
        }
      }
    } catch (error) {
      console.error('Error in deleteShift:', error);
      throw error;
    }
  },

  async verifyShift(shift_id: string, data: {
    ppv_earned?: number;
    noppv_earned?: number;
    verified_by: string;
  }): Promise<void> {
    try {
      const now = DateTime.utc();
      const updated_ata: any = {
        verification_required: false,
        verified_at: now.toISO(),
        verified_by: data.verified_by,
        updated_at: now.toISO()
      };

      if (data.ppv_earned !== undefined) {
        updated_ata.ppv_earned = data.ppv_earned;
      }
      if (data.noppv_earned !== undefined) {
        updated_ata.noppv_earned = data.noppv_earned;
      }

      const { error } = await supabase
        .from('shifts')
        .update(updated_ata)
        .eq('id', shift_id);

      if (error) {
        console.error('Error verifying shift:', error);
        throw error;
      }

      // Log the shift verification
      try {
        await SupabaseShiftLoggingService.logSalesShiftSubmit({
          id: shift_id,
          model_id: '', // We don't have the full shift object here
          user_id: null,
          trainer_id: null,
          start_date_time: DateTime.utc(),
          end_date_time: DateTime.utc(),
          ppv_earned: 0,
          noppv_earned: 0,
          livestream_earned: 0,
          is_livestream_shift: false,
          shift_code: '',
          submitted: false,
          clocked_in: false,
          clock_in_time: undefined,
          created_at: DateTime.utc(),
          updated_at: DateTime.utc(),
          verification_required: false,
          verified_by: data.verified_by,
          verified_at: undefined,
        }, data.verified_by);
      } catch (logError) {
        console.error('Error logging shift verification:', logError);
        // Don't throw here - the shift was verified successfully
      }
    } catch (error) {
      console.error('Error in verifyShift:', error);
      throw error;
    }
  },

  async getUserPeriodStats(user_id: string, startDate: DateTime, endDate: DateTime, otFilter: 'all' | 'ot' | 'no-ot' = 'all'): Promise<{
    byModel: {
      [model_id: string]: {
        totalPPV: number;
        totalNonPPV: number;
        hoursWorked: number;
        earnedPerHour: number;
        noPpvEarnedPerHour: number;
        averagePPVPer8HourShift: number;
        averageNonPPVPer8HourShift: number;
        averagePPVPer12HourShift: number;
        averageNonPPVPer12HourShift: number;
        percentageOfEarnings: number;
        shiftsCount: number;
        workerType: 'weekday' | 'weekend';
      };
    };
    overall: {
      totalPPV: number;
      totalNonPPV: number;
      hoursWorked: number;
      earnedPerHour: number;
      noPpvEarnedPerHour: number;
      averagePPVPer8HourShift: number;
      averageNonPPVPer8HourShift: number;
      averagePPVPer12HourShift: number;
      averageNonPPVPer12HourShift: number;
      totalShifts: number;
      workerType: 'weekday' | 'weekend';
    };
  }> {
    try {
      // Build user shifts query
      const userShiftsFilters: ShiftFilter[] = [
        { field: 'user_id', operator: 'eq', value: user_id },
        { field: 'start_date_time', operator: 'gte', value: startDate.toISO() },
        { field: 'start_date_time', operator: 'lte', value: endDate.toISO() },
        { field: 'shift_code', operator: 'neq', value: 'COVER' },
        { field: 'submitted', operator: 'eq', value: true }
      ];

      // Apply OT filter
      if (otFilter === 'ot') {
        userShiftsFilters.push({ field: 'shift_code', operator: 'in', value: OT_SHIFT_CODES_KEYS });
      } else if (otFilter === 'no-ot') {
        userShiftsFilters.push({ field: 'shift_code', operator: 'not-in', value: OT_SHIFT_CODES_KEYS });
      }

      const userShifts = await SupabaseShiftService.getShifts({ filters: userShiftsFilters });

      // Get unique model IDs from user's shifts
      const modelIds = [...new Set(userShifts.map(shift => shift.model_id))];

      // Get all shifts for these models to calculate percentage of earnings
      const modelTotalPPVMap: Record<string, { ot: number; nonOt: number }> = {};

      if (modelIds.length > 0) {
        // Get all model shifts in chunks (Supabase limit is around 1000 for IN queries)
        const chunkSize = 100;
        const modelIdChunks = Array.from({ length: Math.ceil(modelIds.length / chunkSize) }, (_, i) =>
          modelIds.slice(i * chunkSize, (i + 1) * chunkSize)
        );

        const modelShiftsPromises = modelIdChunks.map(chunk =>
          SupabaseShiftService.getShifts({
            filters: [
              { field: 'model_id', operator: 'in', value: chunk },
              { field: 'start_date_time', operator: 'gte', value: startDate.toISO() },
              { field: 'start_date_time', operator: 'lte', value: endDate.toISO() },
              { field: 'submitted', operator: 'eq', value: true }
            ]
          })
        );

        const modelShiftsResults = await Promise.all(modelShiftsPromises);
        const modelShifts = modelShiftsResults.flat();

        // Calculate total PPV for each model, separating OT and non-OT
        for (const shift of modelShifts) {
          const ppv = shift.ppv_earned || 0;
          const isOT = OT_SHIFT_CODES_KEYS.includes(shift.shift_code);

          if (!modelTotalPPVMap[shift.model_id]) {
            modelTotalPPVMap[shift.model_id] = { ot: 0, nonOt: 0 };
          }

          if (isOT) {
            modelTotalPPVMap[shift.model_id].ot += ppv;
          } else {
            modelTotalPPVMap[shift.model_id].nonOt += ppv;
          }
        }
      }

      // Initialize stats objects
      const byModel: {
        [model_id: string]: {
          totalPPV: number;
          totalNonPPV: number;
          otPPV: number;
          nonOtPPV: number;
          hoursWorked: number;
          shiftsCount: number;
        };
      } = {};

      let overallStats = {
        totalPPV: 0,
        totalNonPPV: 0,
        otPPV: 0,
        nonOtPPV: 0,
        hoursWorked: 0,
        totalShifts: 0,
        weekdayShifts: 0,
        weekendShifts: 0,
      };

      // Process each shift
      userShifts.forEach((shift: Shift) => {
        const model_id = shift.model_id;
        const ppvValue = shift.ppv_earned || 0;
        const nonPPVValue = shift.noppv_earned || 0;
        const hours = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;
        const isOT = OT_SHIFT_CODES_KEYS.includes(shift.shift_code);

        // Initialize model stats if not exists
        if (!byModel[model_id]) {
          byModel[model_id] = {
            totalPPV: 0,
            totalNonPPV: 0,
            otPPV: 0,
            nonOtPPV: 0,
            hoursWorked: 0,
            shiftsCount: 0,
          };
        }

        // Update model-specific stats
        byModel[model_id].totalPPV += ppvValue;
        byModel[model_id].totalNonPPV += nonPPVValue;
        if (isOT) {
          byModel[model_id].otPPV += ppvValue;
        } else {
          byModel[model_id].nonOtPPV += ppvValue;
        }
        byModel[model_id].hoursWorked += hours;
        byModel[model_id].shiftsCount++;

        // Update overall stats
        overallStats.totalPPV += ppvValue;
        overallStats.totalNonPPV += nonPPVValue;
        if (isOT) {
          overallStats.otPPV += ppvValue;
        } else {
          overallStats.nonOtPPV += ppvValue;
        }
        overallStats.hoursWorked += hours;
        overallStats.totalShifts++;

        // Track non-OT shifts for worker type determination
        if (!isOT) {
          const day = shift.start_date_time.weekday % 7;
          if (day === 0 || day === 6) {
            overallStats.weekendShifts++;
          } else {
            overallStats.weekdayShifts++;
          }
        }
      });

      // Calculate final overall stats
      const filteredPPV = otFilter === 'ot'
        ? overallStats.otPPV
        : otFilter === 'no-ot'
          ? overallStats.nonOtPPV
          : overallStats.totalPPV;

      const earnedPerHour = overallStats.hoursWorked > 0 ? filteredPPV / overallStats.hoursWorked : 0;
      const ppvPerHour = overallStats.hoursWorked > 0 ? filteredPPV / overallStats.hoursWorked : 0;
      const nonPPVPerHour = overallStats.hoursWorked > 0 ? overallStats.totalNonPPV / overallStats.hoursWorked : 0;

      // Determine worker type based on non-OT shifts
      let workerType: 'weekday' | 'weekend' = 'weekday';

      if (otFilter === 'no-ot' || otFilter === 'all') {
        const totalNonOTShifts = overallStats.weekdayShifts + overallStats.weekendShifts;
        if (totalNonOTShifts > 0) {
          if (overallStats.weekendShifts / totalNonOTShifts >= 0.75) {
            workerType = 'weekend';
          }
        }
      }

      // Calculate final stats for each model
      const finalByModel: {
        [model_id: string]: {
          totalPPV: number;
          totalNonPPV: number;
          hoursWorked: number;
          earnedPerHour: number;
          noPpvEarnedPerHour: number;
          averagePPVPer8HourShift: number;
          averageNonPPVPer8HourShift: number;
          averagePPVPer12HourShift: number;
          averageNonPPVPer12HourShift: number;
          percentageOfEarnings: number;
          shiftsCount: number;
          workerType: 'weekday' | 'weekend';
        };
      } = {};

      Object.entries(byModel).forEach(([model_id, stats]) => {
        // Skip models that don't have shifts matching the current filter
        if (otFilter === 'ot' && stats.otPPV === 0) return;
        if (otFilter === 'no-ot' && stats.nonOtPPV === 0) return;

        // Get the appropriate PPV values based on the filter
        const userPPV = otFilter === 'ot'
          ? stats.otPPV
          : otFilter === 'no-ot'
            ? stats.nonOtPPV
            : stats.totalPPV;

        const modelPPVTotal = otFilter === 'ot'
          ? modelTotalPPVMap[model_id]?.ot || 0
          : otFilter === 'no-ot'
            ? modelTotalPPVMap[model_id]?.nonOt || 0
            : (modelTotalPPVMap[model_id]?.ot || 0) + (modelTotalPPVMap[model_id]?.nonOt || 0);

        const earnedPerHour = stats.hoursWorked > 0 ? userPPV / stats.hoursWorked : 0;
        const ppvPerHour = stats.hoursWorked > 0 ? userPPV / stats.hoursWorked : 0;
        const nonPPVPerHour = stats.hoursWorked > 0 ? stats.totalNonPPV / stats.hoursWorked : 0;
        const percentOfEarnings = calculatePercentageOfEarnings(userPPV, modelPPVTotal);

        finalByModel[model_id] = {
          totalPPV: userPPV,
          totalNonPPV: stats.totalNonPPV,
          hoursWorked: stats.hoursWorked,
          earnedPerHour: earnedPerHour,
          noPpvEarnedPerHour: nonPPVPerHour,
          averagePPVPer8HourShift: ppvPerHour * 8,
          averageNonPPVPer8HourShift: nonPPVPerHour * 8,
          averagePPVPer12HourShift: ppvPerHour * 12,
          averageNonPPVPer12HourShift: nonPPVPerHour * 12,
          percentageOfEarnings: percentOfEarnings,
          shiftsCount: stats.shiftsCount,
          workerType: workerType
        };
      });

      const finalOverall = {
        totalPPV: filteredPPV,
        totalNonPPV: overallStats.totalNonPPV,
        hoursWorked: overallStats.hoursWorked,
        earnedPerHour: earnedPerHour,
        noPpvEarnedPerHour: nonPPVPerHour,
        averagePPVPer8HourShift: ppvPerHour * 8,
        averageNonPPVPer8HourShift: nonPPVPerHour * 8,
        averagePPVPer12HourShift: ppvPerHour * 12,
        averageNonPPVPer12HourShift: nonPPVPerHour * 12,
        totalShifts: overallStats.totalShifts,
        workerType
      };

      return {
        byModel: finalByModel,
        overall: finalOverall
      };
    } catch (error) {
      console.error('Error in getUserPeriodStats:', error);
      return {
        byModel: {},
        overall: {
          totalPPV: 0,
          totalNonPPV: 0,
          hoursWorked: 0,
          earnedPerHour: 0,
          noPpvEarnedPerHour: 0,
          averagePPVPer8HourShift: 0,
          averageNonPPVPer8HourShift: 0,
          averagePPVPer12HourShift: 0,
          averageNonPPVPer12HourShift: 0,
          totalShifts: 0,
          workerType: 'weekday'
        }
      };
    }
  },
};

// React Query Hooks
export const useShifts = (params: GetShiftsParams = { filters: [], sort: [] }) => {
  return useQuery({
    queryKey: ['supabase-shifts', params],
    queryFn: () => SupabaseShiftService.getShifts(params),
    enabled: params.enabled !== false
  });
};

export const useCreateShift = () => {
  return useMutation({
    mutationFn: async ({ shift, performed_by }: { shift: Omit<Shift, 'id' | 'created_at' | 'updated_at'>; performed_by?: string }) => {
      return SupabaseShiftService.createShift(shift, performed_by);
    },
  });
};

export const useUpdateShift = () => {
  return useMutation({
    mutationFn: ({ shiftId, data, performedBy }: { shiftId: string; data: Partial<Shift>; performedBy?: string }) => {
      return SupabaseShiftService.updateShift(shiftId, data, performedBy);
    }
  });
};

export const useDeleteShift = () => {
  return useMutation({
    mutationFn: ({ shiftId, performedBy }: { shiftId: string; performedBy?: string }) => {
      return SupabaseShiftService.deleteShift(shiftId, performedBy);
    },
  });
};

export const useVerifyShift = () => {
  return useMutation({
    mutationFn: ({ shiftId, data }: {
      shiftId: string;
      data: {
        ppv_earned?: number;
        noppv_earned?: number;
        verified_by: string;
      }
    }) => {
      return SupabaseShiftService.verifyShift(shiftId, data);
    },
  });
};

export const useUserPeriodStats = (user_id: string, start_date: DateTime, end_date: DateTime, ot_filter: 'all' | 'ot' | 'no-ot' = 'all') => {
  const queryKey = useMemo(() =>
    ['supabase-user-period-stats', user_id, start_date.toISO(), end_date.toISO(), ot_filter],
    [user_id, start_date, end_date, ot_filter]
  );

  const queryFn = useCallback(() =>
    SupabaseShiftService.getUserPeriodStats(user_id, start_date, end_date, ot_filter),
    [user_id, start_date, end_date, ot_filter]
  );

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!user_id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false
  });
};

export const useShiftsByModels = (startDate: DateTime, endDate: DateTime, modelIds: string[]) => {
  return useQuery({
    queryKey: ['supabase-shifts', {
      filters: [
        { field: 'model_id', operator: 'in', value: modelIds },
        { field: 'start_date_time', operator: 'gte', value: startDate.toISO() },
        { field: 'start_date_time', operator: 'lte', value: endDate.toISO() }
      ]
    }],
    queryFn: () => {
      if (!modelIds.length) return Promise.resolve([]);

      return SupabaseShiftService.getShifts({
        filters: [
          { field: 'model_id', operator: 'in', value: modelIds },
          { field: 'start_date_time', operator: 'gte', value: startDate.toISO() },
          { field: 'start_date_time', operator: 'lte', value: endDate.toISO() }
        ]
      });
    },
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!startDate && !!endDate && modelIds.length > 0,
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        console.log(`Retrying shifts query (attempt ${failureCount + 1})`);
        console.error(error);
        return true;
      }
      return false;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useBulkUserStats = (startDate: DateTime, endDate: DateTime, otFilter: 'all' | 'ot' | 'no-ot' = 'all') => {
  const params: GetShiftsParams = {
    filters: [
      { field: 'start_date_time', operator: 'gte', value: startDate.toISO() },
      { field: 'start_date_time', operator: 'lte', value: endDate.toISO() },
      { field: 'shift_code', operator: 'neq', value: 'COVER' },
      { field: 'submitted', operator: 'eq', value: true }
    ],
    sort: [{ field: 'user_id', direction: 'asc' }]
  };

  // Add OT filter if specified
  if (otFilter === 'ot') {
    params.filters?.push({ field: 'shift_code', operator: 'in', value: OT_SHIFT_CODES_KEYS });
  } else if (otFilter === 'no-ot') {
    params.filters?.push({ field: 'shift_code', operator: 'not-in', value: OT_SHIFT_CODES_KEYS });
  }

  const { data: shifts = [], isLoading, isError } = useQuery({
    queryKey: ['supabase-bulk-shifts', params],
    queryFn: () => SupabaseShiftService.getShifts(params),
    staleTime: 30 * 1000
  });

  const userStats = useMemo(() => {
    if (isLoading || !shifts.length) return {};

    // First calculate model-wide averages
    const modelStats: Record<string, {
      weekday: {
        totalPPV: number;
        totalNonPPV: number;
        hoursWorked: number;
        shiftsCount: number;
        averagePPVPerHour: number;
        averageNonPPVPerHour: number;
      };
      weekend: {
        totalPPV: number;
        totalNonPPV: number;
        hoursWorked: number;
        shiftsCount: number;
        averagePPVPerHour: number;
        averageNonPPVPerHour: number;
      };
    }> = {};

    // Calculate model-wide stats
    shifts.forEach(shift => {
      if (!shift.model_id) return;

      if (!modelStats[shift.model_id]) {
        modelStats[shift.model_id] = {
          weekday: {
            totalPPV: 0,
            totalNonPPV: 0,
            hoursWorked: 0,
            shiftsCount: 0,
            averagePPVPerHour: 0,
            averageNonPPVPerHour: 0
          },
          weekend: {
            totalPPV: 0,
            totalNonPPV: 0,
            hoursWorked: 0,
            shiftsCount: 0,
            averagePPVPerHour: 0,
            averageNonPPVPerHour: 0
          }
        };
      }

      const hours = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;
      const isWeekend = shift.start_date_time.isWeekend;
      const type = isWeekend ? 'weekend' : 'weekday';

      modelStats[shift.model_id][type].totalPPV += shift.ppv_earned || 0;
      modelStats[shift.model_id][type].totalNonPPV += shift.noppv_earned || 0;
      modelStats[shift.model_id][type].hoursWorked += hours;
      modelStats[shift.model_id][type].shiftsCount++;
    });

    // Calculate averages for each model
    Object.values(modelStats).forEach(stats => {
      ['weekday', 'weekend'].forEach(type => {
        const typeStats = stats[type as 'weekday' | 'weekend'];
        if (typeStats.hoursWorked > 0) {
          typeStats.averagePPVPerHour = typeStats.totalPPV / typeStats.hoursWorked;
          typeStats.averageNonPPVPerHour = typeStats.totalNonPPV / typeStats.hoursWorked;
        }
      });
    });

    // Initialize results object
    const results: Record<string, {
      byModel: Record<string, {
        totalPPV: number;
        totalNonPPV: number;
        hoursWorked: number;
        earnedPerHour: number;
        averagePPVPer8HourShift: number;
        averageNonPPVPer8HourShift: number;
        averagePPVPer12HourShift: number;
        averageNonPPVPer12HourShift: number;
        shiftsCount: number;
        workerType: 'weekday' | 'weekend';
        // New relative performance metrics
        relativePerformance: number;
        relativeNonPPVPerformance: number;
        relativePPVPerformance: number;
      }>;
      overall: {
        totalPPV: number;
        totalNonPPV: number;
        hoursWorked: number;
        earnedPerHour: number;
        averagePPVPer8HourShift: number;
        averageNonPPVPer8HourShift: number;
        averagePPVPer12HourShift: number;
        averageNonPPVPer12HourShift: number;
        totalShifts: number;
        workerType: 'weekday' | 'weekend';
        // New relative performance metrics
        averageRelativePerformance: number;
        averageRelativeNonPPVPerformance: number;
        averageRelativePPVPerformance: number;
      };
    }> = {};

    // Group shifts by user
    const shiftsByUser: Record<string, Shift[]> = {};

    shifts.forEach(shift => {
      if (!shift.user_id) return;

      if (!shiftsByUser[shift.user_id]) {
        shiftsByUser[shift.user_id] = [];
      }

      shiftsByUser[shift.user_id].push(shift);
    });

    // Process each user's shifts
    Object.entries(shiftsByUser).forEach(([user_id, userShifts]) => {
      if (!userShifts.length) return;

      const userResult = {
        byModel: {} as Record<string, any>,
        overall: {
          totalPPV: 0,
          totalNonPPV: 0,
          hoursWorked: 0,
          earnedPerHour: 0,
          noPpvEarnedPerHour: 0,
          averagePPVPer8HourShift: 0,
          averageNonPPVPer8HourShift: 0,
          averagePPVPer12HourShift: 0,
          averageNonPPVPer12HourShift: 0,
          totalShifts: 0,
          workerType: 'weekday' as 'weekday' | 'weekend',
          averageRelativePerformance: 0,
          averageRelativeNonPPVPerformance: 0,
          averageRelativePPVPerformance: 0
        }
      };

      let weekdayShifts = 0;
      let weekendShifts = 0;

      // Process each shift
      userShifts.forEach(shift => {
        const model_id = shift.model_id;

        if (!userResult.byModel[model_id]) {
          userResult.byModel[model_id] = {
            totalPPV: 0,
            totalNonPPV: 0,
            hoursWorked: 0,
            shiftsCount: 0,
            relativePerformance: 0,
            relativeNonPPVPerformance: 0,
            relativePPVPerformance: 0
          };
        }

        const ppvValue = shift.ppv_earned || 0;
        const nonPPVValue = shift.noppv_earned || 0;
        const hours = shift.end_date_time.diff(shift.start_date_time, 'hours').hours;

        // Update model stats
        userResult.byModel[model_id].totalPPV += ppvValue;
        userResult.byModel[model_id].totalNonPPV += nonPPVValue;
        userResult.byModel[model_id].hoursWorked += hours;
        userResult.byModel[model_id].shiftsCount++;

        // Update overall stats
        userResult.overall.totalPPV += ppvValue;
        userResult.overall.totalNonPPV += nonPPVValue;
        userResult.overall.hoursWorked += hours;
        userResult.overall.totalShifts++;

        // Check if OT shift
        const isOT = OT_SHIFT_CODES_KEYS.includes(shift.shift_code);

        // Only count non-OT shifts for worker type determination
        if (!isOT) {
          if (shift.start_date_time.isWeekend) {
            weekendShifts++;
          } else {
            weekdayShifts++;
          }
        }
      });

      // Determine worker type based on non-OT shifts
      const totalNonOTShifts = weekdayShifts + weekendShifts;
      if (totalNonOTShifts > 0 && weekendShifts / totalNonOTShifts >= 0.75) {
        userResult.overall.workerType = 'weekend';
      }

      // Calculate stats for each model including relative performance
      let totalWeightedRelativePerformance = 0;
      let totalWeightedRelativeNonPPVPerformance = 0;
      let totalWeightedRelativePPVPerformance = 0;
      let totalHoursForWeighting = 0;

      Object.entries(userResult.byModel).forEach(([model_id, stats]) => {
        const userModelStats = stats as any;
        const type = userResult.overall.workerType === 'weekend' ? 'weekend' : 'weekday';
        const modelAverages = modelStats[model_id]?.[type];

        if (userModelStats.hoursWorked > 0 && modelAverages) {
          const ppvPerHour = userModelStats.totalPPV / userModelStats.hoursWorked;
          const nonPPVPerHour = userModelStats.totalNonPPV / userModelStats.hoursWorked;

          userModelStats.earnedPerHour = ppvPerHour + nonPPVPerHour;
          userModelStats.noPpvEarnedPerHour = nonPPVPerHour;
          userModelStats.workerType = userResult.overall.workerType;

          // Calculate relative performance metrics
          userModelStats.relativePPVPerformance = modelAverages.averagePPVPerHour > 0
            ? ppvPerHour / modelAverages.averagePPVPerHour
            : 1;

          userModelStats.relativeNonPPVPerformance = modelAverages.averageNonPPVPerHour > 0
            ? nonPPVPerHour / modelAverages.averageNonPPVPerHour
            : 1;

          userModelStats.relativePerformance = (userModelStats.relativePPVPerformance + userModelStats.relativeNonPPVPerformance) / 2;

          // Set average values based on worker type
          if (userResult.overall.workerType === 'weekend') {
            userModelStats.averagePPVPer12HourShift = ppvPerHour * 12;
            userModelStats.averageNonPPVPer12HourShift = nonPPVPerHour * 12;
            userModelStats.averagePPVPer8HourShift = 0;
            userModelStats.averageNonPPVPer8HourShift = 0;
          } else {
            userModelStats.averagePPVPer8HourShift = ppvPerHour * 8;
            userModelStats.averageNonPPVPer8HourShift = nonPPVPerHour * 8;
            userModelStats.averagePPVPer12HourShift = 0;
            userModelStats.averageNonPPVPer12HourShift = 0;
          }

          // Accumulate for weighted overall average
          const hoursWeight = userModelStats.hoursWorked;
          totalWeightedRelativePerformance += userModelStats.relativePerformance * hoursWeight;
          totalWeightedRelativeNonPPVPerformance += userModelStats.relativeNonPPVPerformance * hoursWeight;
          totalWeightedRelativePPVPerformance += userModelStats.relativePPVPerformance * hoursWeight;
          totalHoursForWeighting += hoursWeight;
        }
      });

      // Calculate weighted overall relative performance metrics
      if (totalHoursForWeighting > 0) {
        userResult.overall.averageRelativePerformance = totalWeightedRelativePerformance / totalHoursForWeighting;
        userResult.overall.averageRelativeNonPPVPerformance = totalWeightedRelativeNonPPVPerformance / totalHoursForWeighting;
        userResult.overall.averageRelativePPVPerformance = totalWeightedRelativePPVPerformance / totalHoursForWeighting;
      }

      // Calculate overall hourly and shift averages
      if (userResult.overall.hoursWorked > 0) {
        const ppvPerHour = userResult.overall.totalPPV / userResult.overall.hoursWorked;
        const nonPPVPerHour = userResult.overall.totalNonPPV / userResult.overall.hoursWorked;

        userResult.overall.earnedPerHour = ppvPerHour + nonPPVPerHour;
        userResult.overall.noPpvEarnedPerHour = nonPPVPerHour;

        if (userResult.overall.workerType === 'weekend') {
          userResult.overall.averagePPVPer12HourShift = ppvPerHour * 12;
          userResult.overall.averageNonPPVPer12HourShift = nonPPVPerHour * 12;
          userResult.overall.averagePPVPer8HourShift = 0;
          userResult.overall.averageNonPPVPer8HourShift = 0;
        } else {
          userResult.overall.averagePPVPer8HourShift = ppvPerHour * 8;
          userResult.overall.averageNonPPVPer8HourShift = nonPPVPerHour * 8;
          userResult.overall.averagePPVPer12HourShift = 0;
          userResult.overall.averageNonPPVPer12HourShift = 0;
        }
      }

      results[user_id] = userResult;
    });

    return { userStats: results, modelAverages: modelStats };
  }, [shifts, isLoading]);

  return {
    data: userStats.userStats,
    modelAverages: userStats.modelAverages,
    isLoading,
    isError
  };
};

export const useUserIncompleteShifts = (user_id: string | undefined) => {
  const params: GetShiftsParams = {
    filters: [
      { field: 'user_id', operator: 'eq', value: user_id },
      { field: 'submitted', operator: 'eq', value: false }
    ],
    sort: [{ field: 'start_date_time', direction: 'asc' }]
  };

  return useQuery({
    queryKey: ['supabase-shifts', { filters: params.filters }],
    queryFn: async () => {
      if (!user_id) return [];
      const shifts = await SupabaseShiftService.getShifts(params);
      return shifts;
    },
    enabled: !!user_id,
    staleTime: 30 * 1000
  });
};

export const useUpcomingUnclockedShifts = (now: DateTime) => {
  const params: GetShiftsParams = {
    filters: [
      { field: 'clocked_in', operator: 'eq', value: false },
      { field: 'start_date_time', operator: 'lte', value: now.plus({ minutes: isDST() ? 90 : 30 }).toISO() },
      { field: 'start_date_time', operator: 'gte', value: now.minus({ months: 2 }).toISO() }
    ],
    sort: [{ field: 'start_date_time', direction: 'asc' }]
  };

  return useQuery({
    queryKey: ['supabase-shifts', { filters: params.filters }],
    queryFn: () => SupabaseShiftService.getShifts(params),
    refetchInterval: 60 * 1000,
  });
};

export const useAvailableOvertimeShifts = (now: DateTime) => {
  const params: GetShiftsParams = {
    filters: [
      { field: 'user_id', operator: 'is', value: null },
      { field: 'start_date_time', operator: 'gte', value: now.toISO() },
      { field: 'shift_code', operator: 'in', value: OT_SHIFT_CODES_KEYS }
    ],
    sort: [{ field: 'start_date_time', direction: 'asc' }]
  };

  return useQuery({
    queryKey: ['supabase-shifts', { filters: params.filters }],
    queryFn: () => SupabaseShiftService.getShifts(params),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

export const useStaffWithoutWeekendShifts = () => {
  const now = useMemo(() => DateTime.utc(), []);

  const { data: users = [] } = useUsers();

  // Get the next weekend's dates
  const nextWeekendDates = useMemo(() => {
    // Start with current time in BST
    let today = DateTime.utc();

    // Adjust to 6am today if we're before 6am, or 6am tomorrow if we're after 6am
    const sixAM = today.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
    if (today < sixAM) {
      today = sixAM;
    } else {
      today = sixAM.plus({ days: 1 });
    }

    // Find the most recent Saturday 6am
    let saturday = today;
    while (saturday.weekday !== 6) {
      saturday = saturday.minus({ days: 1 });
    }

    // If we're past Monday 6am, move to next weekend
    const mondayEnd = saturday.plus({ days: 2 }); // Monday 6am
    if (today > mondayEnd) {
      saturday = saturday.plus({ days: 7 }); // Move to next Saturday
    }

    // Create the weekend range: Saturday 6am to Monday 6am
    const weekendStart = saturday;
    const weekendEnd = saturday.plus({ days: 2 }); // Monday 6am

    return [weekendStart, weekendEnd];
  }, [now]);

  // Get shifts for the next weekend
  const params: GetShiftsParams = {
    filters: [
      { field: 'start_date_time', operator: 'gte', value: nextWeekendDates[0].toISO() },
      { field: 'start_date_time', operator: 'lte', value: nextWeekendDates[1].toISO() }
    ],
    sort: [{ field: 'start_date_time', direction: 'asc' }]
  };

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ['supabase-shifts', params],
    queryFn: () => SupabaseShiftService.getShifts(params),
    staleTime: 30 * 1000
  });

  // Filter for FP permanent staff without weekend shifts
  const staffWithoutShifts = useMemo(() => {
    // Get all FP permanent staff (excluding Trial role) with the specified roleId
    const fpStaff = users.filter(user =>
      user.pay_class === 'FP' &&
      !user.deactivated_at && // Exclude deactivated users
      user.roles.includes('U4tIqPzXgRWXFl0NanRs') // Include only users with the specified role
    );

    // Get users who have shifts scheduled
    const usersWithShifts = new Set(shifts.map(shift => shift.user_id));

    // Return staff who don't have shifts
    return fpStaff.filter(user => !usersWithShifts.has(user.id));
  }, [users, shifts]);

  return {
    staffWithoutShifts,
    nextWeekendDates,
    isLoading
  };
};

export default SupabaseShiftService;
